import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Badge } from "flowbite-react";
import { ReceiptEuro } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
  ExpenseSearch,
  useExpenses,
  useExpenseSearch,
  VouchersAndExpensesList,
} from "@/features/vouchers";
import { VoucherDrawerHeader } from "@/features/vouchers/components/VoucherDetailsDrawer/VoucherDrawerHeader";
import { VoucherSummaryCard } from "@/features/vouchers/components/VoucherDetailsDrawer/VoucherSummaryCard";
import type { VoucherEmployee } from "@/features/vouchers/types/voucherEmployee.type";
import { VoucherUsageBanner } from "@/shared/components/voucherUsageBanner";

type VoucherDetailsDrawerProps = {
  isOpen: boolean
  onClose: () => void
  voucher: VoucherEmployee | null
};

export const VoucherDetailsDrawer = ({
  isOpen,
  onClose,
  voucher,
}: VoucherDetailsDrawerProps) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const { data: expensesData, isLoading: isLoadingExpenses } = useExpenses(voucher?.id);
  const { searchAnExpense, filteredExpenses, handleSearchChange } = useExpenseSearch({
    expenses: expensesData?.expenses ?? [],
    resetTrigger: voucher?.id ?? "",
  });

  const totalExpenses = expensesData?.metadata?.total_items ?? 0;

  useEffect(() => {
    if (!isOpen) return undefined;

    previousActiveElement.current = document.activeElement as HTMLElement;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (event.key === "Tab" && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
        else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    setTimeout(() => {
      const firstFocusable = drawerRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }, 100);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
      previousActiveElement.current?.focus();
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!voucher) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label={`Détails du bon ${voucher.voucher_campaign_name}`}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleBackdropClick}
          />

          <motion.div
            ref={drawerRef}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute right-0 top-0 h-full w-full sm:w-[85%] md:w-[75%] lg:w-[600px] xl:w-[650px] bg-white dark:bg-slate-900 shadow-2xl flex flex-col"
          >
            <VoucherDrawerHeader
              voucher={voucher}
              onClose={onClose}
            />

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              <VoucherSummaryCard voucher={voucher} />

              {voucher.event && (
                <VoucherUsageBanner
                  event={voucher.event}
                  mascotThumbnailUrl={voucher.mascot_thumbnail_url}
                />
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <ExpenseSearch
                      searchAnExpense={searchAnExpense}
                      onSearchChange={handleSearchChange}
                    />
                  </div>

                  <Badge color="muted" size="sm" className="hidden sm:flex p-2.5! flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <ReceiptEuro size={14} />
                      <span>{`${totalExpenses} dépenses`}</span>
                    </div>
                  </Badge>
                </div>

                <VouchersAndExpensesList
                  expenses={filteredExpenses}
                  isLoadingExpenses={isLoadingExpenses}
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

VoucherDetailsDrawer.displayName = "VoucherDetailsDrawer";
